import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { MemberRole, ReadScimGroupMapping } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';
import { invalidateScimQueries } from './invalidate-scim-queries';

export type UpdateScimGroupMappingParams = {
  organizationId: string;
  groupId: string;
  /** The role the group maps to; `null` deletes the mapping. */
  role: MemberRole | null;
  /** Required by the API to map a group to `admin` (400, 5703 without it). */
  confirmAdmin?: boolean;
};

export const updateScimGroupMapping = async (
  params: UpdateScimGroupMappingParams,
): Promise<ReadScimGroupMapping | void> => {
  const { organizationId, groupId, role, confirmAdmin } = params;
  if (role === null) {
    return await tinctApi.scim.deleteScimGroupMapping(organizationId, groupId);
  }
  return await tinctApi.scim.updateScimGroupMapping(organizationId, groupId, {
    role,
    ...(confirmAdmin ? { confirmAdmin } : {}),
  });
};

/**
 * Maps a directory group to a role, or to nothing (`role: null`, idempotent). The members of the
 * group get their role recomputed, so the member lists are refreshed too. 409 (5704) when the change
 * would leave the workspace without an admin: its message says so.
 */
export const useUpdateScimGroupMapping = (
  options?: Omit<
    UseMutationOptions<
      ReadScimGroupMapping | void,
      ApiError,
      UpdateScimGroupMappingParams
    >,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateScimGroupMapping,
    onSuccess: (...args) => {
      const { organizationId } = args[1];
      invalidateScimQueries(queryClient, organizationId, 'group-mappings');
      // Roles follow the mappings: the members' roles and `roleManaged` flags have moved
      queryClient.invalidateQueries({ queryKey: ['organization-members', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization-member', organizationId] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
